import React, { useState, useEffect, useCallback } from "react";
import ExcelJS from "exceljs";
import { Grid as GridLoading } from "react-loader-spinner";
// import { Tooltip as ReactTooltip } from "react-tooltip";
import { styled } from "@mui/material/styles";
import SlotCounter from "react-slot-counter";

// Import MUI elements
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

// Import framework to manage datepicker
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// Import icons
import AssistantPhotoIcon from "@mui/icons-material/AssistantPhoto";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CallIcon from "@mui/icons-material/Call";
import CategoryIcon from "@mui/icons-material/Category";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import SentimentNeutralIcon from "@mui/icons-material/SentimentNeutral";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import SummarizeIcon from "@mui/icons-material/Summarize";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    fontSize: 13,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const ModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 900,
  height: 650,
  bgcolor: "background.paper",
  border: "1px solid #000",
  p: 4,
  overflow: "scroll",
  display: "block",
};

function unixTimestampToString(timestampString: string) {
  var timestamp = parseInt(timestampString);

  var milliseconds = timestamp * 1000;

  var dateObject = new Date(milliseconds);

  var month = dateObject.getMonth() + 1; // Adding 1 because January is 0
  var day = dateObject.getDate();
  var year = dateObject.getFullYear();

  var formattedDate =
    month.toString().padStart(2, "0") +
    "-" +
    day.toString().padStart(2, "0") +
    "-" +
    year.toString();

  return formattedDate;
}

function convertToMMSS(number: number) {
  let minutes = Math.floor(number);
  let secondsDecimal = number - minutes;
  let seconds = Math.round(secondsDecimal * 60);

  let formattedTime =
    ("0" + minutes).slice(-2) + ":" + ("0" + seconds).slice(-2);

  return formattedTime;
}

function Search({ parentToChild }: { parentToChild: any }) {
  const { startDate, endDate, mode } = parentToChild;

  const [insightsModalOpen, setInsightsModalOpen] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]); // Ensure data is initialized as an empty array
  const [error, setError] = useState<any>(null);

  const [inputValue, setInputValue] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const [selectedTranscript, setSelectedTranscript] = useState<[string, any[]]>(
    ["", []]
  );
  const [summaryNoteData, setSummaryNoteData] = useState<any[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isNoteLoading, setIsNoteLoading] = useState<boolean>(true);

  // Variables for sending a report
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [flag, setFlag] = useState<boolean>(false);
  const [subject, setSubject] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [comments, setComments] = useState<string>("");
  const [frequency, setFrequency] = useState<string>("Daily");
  const [recurrences, setRecurrences] = useState<string>("4");
  const [rangeStartDate, setRangeStartDate] = useState<dayjs.Dayjs>(dayjs());
  const [rangeEndDate, setRangeEndDate] = useState<dayjs.Dayjs>(dayjs());
  const [excelFile, setExcelFile] = useState<any | null>(null);
  const Today = dayjs();

  useEffect(() => {
    if (
      rangeStartDate &&
      rangeEndDate &&
      rangeStartDate.isAfter(rangeEndDate, "day")
    ) {
      setRangeStartDate(rangeEndDate);
    }
  }, [rangeStartDate, rangeEndDate]);

  // Handle excel file export
  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet 1");

    // Add headers
    const headers = data && Object.keys(data[0]);
    headers && worksheet.addRow(headers);

    // Add data rows
    data?.forEach((row: any) => {
      const rowData: any[] = []; // Explicitly define the type of rowData
      Object.values(row).forEach((cell) => {
        rowData.push(cell);
      });
      worksheet.addRow(rowData);
    });

    const excelBuffer = await workbook.xlsx.writeBuffer();

    setExcelFile(excelBuffer);
  };

  const handlePreview = () => {
    const blob = new Blob([excelFile], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "preview.xlsx";
    a.click();

    URL.revokeObjectURL(url);
  };

  const fetchData = useCallback(
    async (data: any) => {
      setIsLoading(true);
      const formattedStartDate = startDate.toISOString().split("T")[0];
      const formattedEndDate = endDate.toISOString().split("T")[0];
      try {
        const response = await fetch(
          `/search?start_date=${formattedStartDate}&end_date=${formattedEndDate}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              keyword: data,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const result = await response.json();
        setData(Array.isArray(result) ? result : []); // Ensure data is always an array
        setIsLoading(false);
      } catch (err) {
        setError(err || "Something went wrong");
        setData([]); // Set data to an empty array in case of error
        setIsLoading(false);
      }
    },
    [startDate, endDate]
  );

  const [callSentiment, setCallSentiment] = useState<any>(null);
  const [callType, setCallType] = useState<any>(null);
  const [nextSteps, setNextSteps] = useState<any>(null);
  const [product, setProduct] = useState<any>(null);
  const [reason, setReason] = useState<any>(null);
  const [resolved, setResolved] = useState<any>(null);
  const [subreason, setSubreason] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);

  // Get insights data
  useEffect(() => {
    // let controller = new AbortController();
    // let signal = controller.signal;

    setIsNoteLoading(true);
    setSummaryNoteData([]);
    const fetchData = async () => {
      try {
        const response = await fetch(
          `/generate_summary_note?transcript=${selectedTranscript[1]}`,
          {
            method: "POST",
          }
        );

        const result = await response.json();
        // console.log(result);
        setSummaryNoteData(Object.entries(result));

        const callSentimentKey = Object.keys(await result).find(
          (key) => key.toLowerCase() === "call sentiment"
        );
        const callTypeKey = Object.keys(await result).find(
          (key) => key.toLowerCase() === "call type"
        );
        const nextStepsKey = Object.keys(await result).find(
          (key) => key.toLowerCase() === "next steps"
        );
        const productKey = Object.keys(await result).find(
          (key) => key.toLowerCase() === "product"
        );
        const reasonKey = Object.keys(await result).find(
          (key) => key.toLowerCase() === "reason"
        );
        const resolvedKey = Object.keys(await result).find(
          (key) => key.toLowerCase() === "resolved"
        );
        const subreasonKey = Object.keys(await result).find(
          (key) => key.toLowerCase() === "subreason"
        );
        const summaryKey = Object.keys(await result).find(
          (key) => key.toLowerCase() === "summary"
        );

        const callSentimentArray = callSentimentKey
          ? await result[callSentimentKey]
          : null;
        const callTypeArray = callTypeKey ? await result[callTypeKey] : null;
        const nextStepsArray = nextStepsKey ? await result[nextStepsKey] : null;
        const productArray = productKey ? await result[productKey] : null;
        const reasonArray = reasonKey ? await result[reasonKey] : null;
        const resolvedArray = resolvedKey ? await result[resolvedKey] : null;
        const subreasonArray = subreasonKey ? await result[subreasonKey] : null;
        const summaryArray = summaryKey ? await result[summaryKey] : null;

        setCallSentiment(callSentimentArray);
        setCallType(callTypeArray);
        setNextSteps(nextStepsArray);
        setProduct(productArray);
        setReason(reasonArray);
        setResolved(resolvedArray);
        setSubreason(subreasonArray);
        setSummary(summaryArray);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData()
      .then(() => setIsNoteLoading(false))
      .catch((error) => console.error("Error in useEffect:", error));

    // return () => {
    //     controller.abort();
    //     controller = new AbortController();
    // };
  }, [selectedTranscript]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();
    fetchData(inputValue);
  };

  const cleanTranscript = (transcript: string) => {
    let cleanTranscript = transcript.replace(/\*/g, "");

    // Find the index of the first occurrence of "Agent"
    let agentIndex = cleanTranscript.indexOf("Agent:");

    // If "Agent" is found, extract the substring starting from that index
    let finalTranscript;
    if (agentIndex !== -1) {
      finalTranscript = cleanTranscript.substring(agentIndex);
      return finalTranscript;
    } else {
      return cleanTranscript;
    }
  };

  const handleOpen = (transcript_id: any, transcript: any) => {
    let cleanTranscript = transcript.replace(/\*/g, "");

    let agentIndex = cleanTranscript.indexOf("Agent:");

    let finalTranscript;
    if (agentIndex !== -1) {
      finalTranscript = cleanTranscript.substring(agentIndex);
    } else {
      finalTranscript = cleanTranscript;
    }

    const transcriptArray = finalTranscript
      .split(/(?=Agent(\s[A-Za-z]+)?:|Customer:)/)
      .filter(
        (segment: string) =>
          segment !== undefined && segment.trim().split(/\s+/).length > 1
      ) // Filter out undefined segments
      .map((segment: string, index: number) => {
        if (index % 2 === 0) {
          return segment.trim();
        } else {
          return `\n${segment}`;
        }
      });

    // Remove the first element of the array if it starts with '[Agent]'
    // if (transcriptArray[0] === '[Agent]') {
    //     transcriptArray.shift();
    // }

    setSelectedTranscript([transcript_id, transcriptArray]);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setExcelFile(null);
  };

  const handleInsightsButtonClick = () => {
    setOpen(false);
    setInsightsModalOpen(true);
  };

  const handleClick = (transcript_id: any, transcript: any) => {
    const transcriptArray = transcript
      .split(/(?=Agent:|Customer:)/)
      .map((segment: string, index: number) => {
        if (index % 2 === 0) {
          return segment.trim();
        } else {
          return `\n${segment}`;
        }
      });

    setSelectedTranscript([transcript_id, transcriptArray]);
    setInsightsModalOpen(true);
  };

  const [groupInsightsData, setGroupInsightsData] = useState<any>(null);
  const [groupInsightsKeyword, setGroupInsightsKeyword] = useState<string>("");
  const [groupInsightsModalOpen, setGroupInsightsModalOpen] =
    useState<boolean>(false);
  const [isGroupInsightsLoading, setIsGroupInsightsLoading] =
    useState<boolean>(true);

  const [commonProducts, setCommonProducts] = useState<any>([]);
  const [commonCauses, setCommonCauses] = useState<any>([]);
  const [commonThemes, setCommonThemes] = useState<any>([]);
  const [generalSummary, setGeneralSummary] = useState<any>([]);
  const [issuesIdentified, setIssuesIdentified] = useState<any>([]);

  const [dashboardStats, setDashboardStats] = useState<any>(null);

  // Get group insights data
  useEffect(() => {
    setIsGroupInsightsLoading(true);
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];
    const fetchData = async () => {
      try {
        const url = `/group_insights?keyword=${inputValue}&start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
        const response = await fetch(url);
        const result = await response.json();
        // console.log(result);
        const resultArray = [Object.values(await result)];
        setGroupInsightsData(resultArray);

        // Create arrays for each corresponding array in the JSON result
        const commonProductsKey = Object.keys(await result).find(
          (key) => key.toLowerCase() === "common products"
        );
        const commonCausesKey = Object.keys(await result).find(
          (key) =>
            key.toLowerCase() === "common causes for customer dissatisfaction"
        );
        const commonThemesKey = Object.keys(await result).find(
          (key) => key.toLowerCase() === "common themes on these calls"
        );
        const generalSummaryKey = Object.keys(await result).find(
          (key) => key.toLowerCase() === "general summary"
        );
        const issuesIdentifiedKey = Object.keys(await result).find(
          (key) => key.toLowerCase() === "general issues identified"
        );

        const commonProductsArray = commonProductsKey
          ? await result[commonProductsKey]
          : null;
        const commonCausesArray = commonCausesKey
          ? await result[commonCausesKey]
          : null;
        const commonThemesArray = commonThemesKey
          ? await result[commonThemesKey]
          : null;
        const generalSummaryArray = generalSummaryKey
          ? await result[generalSummaryKey]
          : null;
        const issuesIdentifiedArray = issuesIdentifiedKey
          ? await result[issuesIdentifiedKey]
          : null;

        setCommonProducts(commonProductsArray);
        setCommonCauses(commonCausesArray);
        setCommonThemes(commonThemesArray);
        setGeneralSummary(generalSummaryArray);
        setIssuesIdentified(issuesIdentifiedArray);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData()
      .then(() => {
        setIsGroupInsightsLoading(false);
      })
      .catch((error) => console.error("Error in useEffect:", error));
    // eslint-disable-next-line
  }, [groupInsightsKeyword, startDate, endDate]);

  // Send report button modal
  const handleSendReport = () => {
    //console.log('Sending report:', { email, comments });
    setIsModalOpen(false);
  };

  const openModal = () => {
    setFlag((flag) => !flag);
    setRangeStartDate(endDate.clone().subtract(1, "days"));
    setRangeEndDate(endDate);
    setSubject(inputValue);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSubject("");
    setInputValue("");
    setFrequency("Daily");
    setIsModalOpen(false);
  };

  // Insights buttons modal
  const handleCloseInsightsModal = () => setInsightsModalOpen(false);

  const handleCloseGroupInsightsModal = () => setGroupInsightsModalOpen(false);

  useEffect(() => {
    var init = "";
    fetchData(init);
  }, [fetchData, startDate, endDate]);

  error !== null && console.log(error);

  // Get overview data with proxy
  useEffect(() => {
    setDashboardStats(null);
    // const thisWeekEndDate = new Date();
    const formattedThisWeekEndDate = rangeEndDate.toISOString().split("T")[0];

    Promise.all([
      fetch(
        `/operations_dashboard_stats?query=${
          inputValue || "a"
        }&end_date=${formattedThisWeekEndDate}`
      ),
    ])
      .then(([resDashboardStats]) => Promise.all([resDashboardStats.json()]))
      .then(([dataDashboardStats]) => {
        setDashboardStats(dataDashboardStats);
      });
  }, [flag, rangeEndDate, rangeStartDate]);

  return (
    <div className='ops-search-wrapper'>
      <div className='exec-search'>
        <Box component='form' noValidate autoComplete='off'>
          <TextField
            // data-tooltip-id='ops-tooltip'
            className='search-form'
            id='outlined-basic'
            label='Search for a specific type of call'
            // label='Type your text here'
            variant='outlined'
            type='text'
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSubmit(e);
              }
            }}
          />
          <IconButton
            size='small'
            onClick={(e) => {
              handleSubmit(e);
            }}
          >
            <SearchIcon fontSize='inherit' />
          </IconButton>
        </Box>
        <div className='ops-search-buttons'>
          <Button
            className='search-button'
            variant='contained'
            endIcon={<SendIcon />}
            onClick={() => {
              handleExport();
              openModal();
            }}
          >
            Send Report
          </Button>
          <Button
            className='search-button'
            variant='contained'
            endIcon={<AutoAwesomeIcon />}
            onClick={() => {
              setGroupInsightsKeyword(inputValue);
              setGroupInsightsModalOpen(true);
            }}
          >
            AI Group Insights
          </Button>
        </div>
      </div>

      <div>
        {data && !isLoading && data.length > 0 ? (
          <TableContainer component={Paper} sx={{ maxHeight: 550 }}>
            <Table
              sx={{ minWidth: 500 }}
              stickyHeader
              aria-label='sticky table'
              id='myTable'
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell className='table-header' align='center'>
                    Date
                  </StyledTableCell>
                  <StyledTableCell className='table-header' align='center'>
                    Transcript ID
                  </StyledTableCell>
                  {/* <StyledTableCell className='table-header' align='center'>
                    Call Type
                    {mode === "dark" ? (
                      <span className='shine'>
                        <AutoAwesomeIcon />
                      </span>
                    ) : (
                      <span className='ai-driven'>
                        <AutoAwesomeIcon />
                      </span>
                    )}
                  </StyledTableCell> */}
                  <StyledTableCell className='table-header' align='center'>
                    Product
                    {mode === "dark" ? (
                      <span className='shine'>
                        <AutoAwesomeIcon />
                      </span>
                    ) : (
                      <span className='ai-driven'>
                        <AutoAwesomeIcon />
                      </span>
                    )}
                  </StyledTableCell>
                  <StyledTableCell className='table-header' align='center'>
                    Duration
                  </StyledTableCell>
                  <StyledTableCell className='table-header' align='center'>
                    Transcript{" "}
                    <span style={{ fontSize: 10 }}>(Click to view)</span>
                  </StyledTableCell>
                  <StyledTableCell className='table-header' align='center'>
                    Sentiment
                    {mode === "dark" ? (
                      <span className='shine'>
                        <AutoAwesomeIcon />
                      </span>
                    ) : (
                      <span className='ai-driven'>
                        <AutoAwesomeIcon />
                      </span>
                    )}
                    <br />
                    <span style={{ fontSize: 10 }}>1 - 10</span>
                  </StyledTableCell>
                  <StyledTableCell className='table-header' align='center'>
                    Additional Insights
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row: any) => (
                  <StyledTableRow key={row["Transcript ID"]}>
                    <StyledTableCell style={{ width: "7%" }} align='center'>
                      {unixTimestampToString(row["Date"])}
                    </StyledTableCell>
                    <StyledTableCell style={{ width: "7%" }} align='center'>
                      {row["Transcript ID"]}
                    </StyledTableCell>
                    {/* <StyledTableCell style={{ width: "7%" }} align='center'>
                      {row["Call Type"]}
                    </StyledTableCell> */}
                    <StyledTableCell style={{ width: "7%" }} align='center'>
                      {row["Product"]}
                    </StyledTableCell>
                    <StyledTableCell style={{ width: "7%" }} align='center'>
                      {convertToMMSS(row["Call Duration"])}
                    </StyledTableCell>
                    <StyledTableCell style={{ width: "15%" }} align='center'>
                      <div
                        className='table-transcript-link'
                        onClick={() =>
                          handleOpen(row["Transcript ID"], row["Transcript"])
                        }
                      >
                        {cleanTranscript(row["Transcript"].substr(0, 200)) +
                          "..."}
                      </div>
                    </StyledTableCell>
                    <StyledTableCell style={{ width: "7%" }} align='center'>
                      {row["Call Sentiment"]}
                    </StyledTableCell>
                    <StyledTableCell style={{ width: "13%" }} align='center'>
                      <Button
                        className='modal-btn'
                        variant='contained'
                        endIcon={<AutoAwesomeIcon />}
                        onClick={() =>
                          handleClick(row["Transcript ID"], row["Transcript"])
                        }
                      >
                        AI Insights
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : isLoading ? (
          <div className='search-loading-area'>
            <Skeleton width='100%' height='200px' />
            <span className='centered-text'>
              <GridLoading
                visible={true}
                height='15'
                width='15'
                color={mode === "dark" ? "white" : "black"}
                ariaLabel='grid-loading'
                radius='12.5'
                wrapperStyle={{}}
                wrapperClass='grid-wrapper'
              />
              Retrieving records...
            </span>
          </div>
        ) : (
          data &&
          data.length === 0 && (
            <div className='no-records-found'>
              <span>No records found.</span>
            </div>
          )
        )}
      </div>
      {groupInsightsData && (
        <Modal
          open={groupInsightsModalOpen}
          onClose={handleCloseGroupInsightsModal}
          aria-labelledby='modal-modal-title'
          aria-describedby='modal-modal-description'
          className='modal-global'
        >
          <Box className='modal-style' sx={ModalStyle}>
            <div className='modal-header'>
              <h3>
                <AutoAwesomeIcon /> AI Group Insights
              </h3>
              <Button
                className='modal-btn'
                variant='contained'
                onClick={handleCloseGroupInsightsModal}
              >
                Close
              </Button>
            </div>
            <div id='modal-modal-description'>
              {!isGroupInsightsLoading &&
              groupInsightsData &&
              groupInsightsData[0].length === 5 &&
              groupInsightsData[0][0].length > 0 ? (
                <Box sx={{ flexGrow: 1 }}>
                  <Grid container spacing={2} className='modal-summary-notes'>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={12}
                      lg={6}
                      xl={6}
                      className='summary-card'
                    >
                      <Card
                        variant='outlined'
                        className='get-insights-card group-insights'
                      >
                        <CardContent>
                          <h4>
                            <CategoryIcon />
                            Common Products
                          </h4>
                          <ul>
                            {Array.isArray(commonProducts) &&
                            commonProducts.length > 0 ? (
                              commonProducts.map(
                                (value: any, index: number) => (
                                  <li key={index}>{value}</li>
                                )
                              )
                            ) : (
                              <li>No data available</li>
                            )}
                          </ul>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={12}
                      lg={6}
                      xl={6}
                      className='summary-card'
                    >
                      <Card
                        variant='outlined'
                        className='get-insights-card group-insights'
                      >
                        <CardContent>
                          <h4>
                            <SentimentVeryDissatisfiedIcon />
                            Common causes for customer dissatisfaction
                          </h4>
                          <ul>
                            {Array.isArray(commonCauses) &&
                            commonCauses.length > 0 ? (
                              commonCauses.map((value: any, index: number) => (
                                <li key={index}>{value}</li>
                              ))
                            ) : (
                              <li>No data available</li>
                            )}
                          </ul>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={12}
                      lg={6}
                      xl={6}
                      className='summary-card'
                    >
                      <Card
                        variant='outlined'
                        className='get-insights-card group-insights'
                      >
                        <CardContent>
                          <h4>
                            <QuestionAnswerIcon />
                            Common themes
                          </h4>
                          <ul>
                            {Array.isArray(commonThemes) &&
                            commonThemes.length > 0 ? (
                              commonThemes.map((value: any, index: number) => (
                                <li key={index}>{value}</li>
                              ))
                            ) : (
                              <li>No data available</li>
                            )}
                          </ul>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={12}
                      lg={6}
                      xl={6}
                      className='summary-card'
                    >
                      <Card
                        variant='outlined'
                        className='get-insights-card group-insights'
                      >
                        <CardContent>
                          <h4>
                            <ReportProblemIcon />
                            Topics identified
                          </h4>
                          <ul>
                            {Array.isArray(issuesIdentified) &&
                            issuesIdentified.length > 0 ? (
                              issuesIdentified.map(
                                (value: any, index: number) => (
                                  <li key={index}>{value}</li>
                                )
                              )
                            ) : (
                              <li>No data available</li>
                            )}
                          </ul>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={12}
                      lg={12}
                      xl={12}
                      className='summary-card'
                    >
                      <Card
                        variant='outlined'
                        className='get-insights-card group-insights'
                      >
                        <CardContent>
                          <h4>
                            <SummarizeIcon />
                            General summary
                          </h4>
                          {typeof generalSummary === "string" &&
                          generalSummary.length > 0 ? (
                            <p>{generalSummary}</p>
                          ) : Array.isArray(generalSummary) &&
                            generalSummary.length > 0 ? (
                            <ul>
                              {generalSummary.map(
                                (value: any, index: number) => (
                                  <li key={index}>{value}</li>
                                )
                              )}
                            </ul>
                          ) : (
                            <p>No data available</p>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <div className='position-reletive'>
                  <Skeleton width='100%' height='450px' />
                  <span className='centered-text'>
                    <GridLoading
                      visible={true}
                      height='15'
                      width='15'
                      color={mode === "dark" ? "white" : "black"}
                      ariaLabel='grid-loading'
                      radius='12.5'
                      wrapperStyle={{}}
                      wrapperClass='grid-wrapper'
                    />
                    AI is generating group insights...
                  </span>
                </div>
              )}
            </div>
          </Box>
        </Modal>
      )}
      {selectedTranscript[1].length > 0 && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby='modal-modal-title'
          aria-describedby='modal-modal-description'
          className='modal-global'
        >
          <Box className='modal-style' sx={ModalStyle}>
            <div className='modal-header'>
              <h3>
                <SummarizeIcon /> Full Transcript
              </h3>
              <Button
                className='modal-btn'
                variant='contained'
                endIcon={<AutoAwesomeIcon />}
                onClick={handleInsightsButtonClick}
              >
                AI Insights
              </Button>
            </div>
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Card variant='outlined' className='get-insights-card'>
                    <CardContent>
                      <Typography
                        id='modal-modal-description transcript-show-modal'
                        sx={{ mt: 2 }}
                      >
                        {selectedTranscript[1].map((segment, index) => (
                          <React.Fragment key={index}>
                            {segment}
                            <br />
                            <br />
                          </React.Fragment>
                        ))}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Modal>
      )}
      <Modal
        className='modal-global'
        open={insightsModalOpen}
        onClose={handleCloseInsightsModal}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box className='modal-style' sx={ModalStyle}>
          <div className='modal-header'>
            <h3>
              <AutoAwesomeIcon /> AI Insights
            </h3>
            <Button
              className='modal-btn'
              variant='contained'
              onClick={handleCloseInsightsModal}
            >
              Close
            </Button>
          </div>
          <div id='modal-modal-description'>
            {!isNoteLoading && summaryNoteData.length > 1 ? (
              // summaryNoteData.map(([key, value], index) => ())
              <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={2} className='modal-summary-notes'>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={6}
                    xl={6}
                    className='summary-card'
                  >
                    <Card variant='outlined' className='get-insights-card'>
                      <CardContent>
                        <h4>
                          <SentimentNeutralIcon />
                          Call Sentiment
                        </h4>
                        <span>{callSentiment ? callSentiment : "No data"}</span>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={6}
                    xl={6}
                    className='summary-card'
                  >
                    <Card variant='outlined' className='get-insights-card'>
                      <CardContent>
                        <h4>
                          <CallIcon />
                          Call Type
                        </h4>
                        <span>{callType ? callType : "No data"}</span>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={6}
                    xl={6}
                    className='summary-card'
                  >
                    <Card variant='outlined' className='get-insights-card'>
                      <CardContent>
                        <h4>
                          <CategoryIcon />
                          Product
                        </h4>
                        <span>{product ? product : "No data"}</span>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={6}
                    xl={6}
                    className='summary-card'
                  >
                    <Card variant='outlined' className='get-insights-card'>
                      <CardContent>
                        <h4>
                          {resolved === "Yes" ? (
                            <CheckCircleOutlineIcon />
                          ) : (
                            <CancelIcon />
                          )}
                          Resolved
                        </h4>
                        <span>{resolved ? resolved : "No data"}</span>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={6}
                    xl={6}
                    className='summary-card'
                  >
                    <Card variant='outlined' className='get-insights-card'>
                      <CardContent>
                        <h4>
                          <ContactSupportIcon />
                          Reason
                        </h4>
                        <span>{reason ? reason : "No data"}</span>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={6}
                    xl={6}
                    className='summary-card'
                  >
                    <Card variant='outlined' className='get-insights-card'>
                      <CardContent>
                        <h4>
                          <ContactSupportIcon />
                          Subreason
                        </h4>
                        <span>{subreason ? subreason : "No data"}</span>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    className='summary-card'
                  >
                    <Card variant='outlined' className='get-insights-card'>
                      <CardContent>
                        <h4>
                          <SummarizeIcon />
                          Summary
                        </h4>
                        <span>{summary ? summary : "No data"}</span>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    className='summary-card'
                  >
                    <Card variant='outlined' className='get-insights-card'>
                      <CardContent>
                        <h4>
                          <AssistantPhotoIcon />
                          Next Steps
                        </h4>
                        <span>{nextSteps ? nextSteps : "No data"}</span>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <div className='position-reletive'>
                <Skeleton width='100%' height='450px' />
                <span className='centered-text'>
                  <GridLoading
                    visible={true}
                    height='15'
                    width='15'
                    color={mode === "dark" ? "white" : "black"}
                    ariaLabel='grid-loading'
                    radius='12.5'
                    wrapperStyle={{}}
                    wrapperClass='grid-wrapper'
                  />
                  AI is generating insights...
                </span>
              </div>
            )}
          </div>
        </Box>
      </Modal>
      <Modal
        className='exec-send-modal modal-global'
        open={isModalOpen}
        onClose={closeModal}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box className='modal-style' sx={ModalStyle}>
          <h3>
            <SendIcon /> Report Form
          </h3>
          <div>
            <TextField
              label='Subject'
              variant='outlined'
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              margin='normal'
            />
            <TextField
              label='Email'
              variant='outlined'
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin='normal'
            />
            <FormControl fullWidth className='send-report-dropdown'>
              <InputLabel id='select-label'>Report Frequency</InputLabel>
              <Select
                labelId='select-label'
                id='select'
                value={frequency}
                onChange={(e) => {
                  setFrequency(e.target.value);

                  // Conditionally update rangeStartDate based on frequency
                  if (e.target.value === "Daily" && rangeEndDate) {
                    setRangeStartDate(rangeEndDate.clone().subtract(1, "days"));
                  } else if (e.target.value === "Weekly" && rangeEndDate) {
                    setRangeStartDate(rangeEndDate.clone().subtract(7, "days"));
                  } else if (e.target.value === "Monthly" && rangeEndDate) {
                    setRangeStartDate(
                      rangeEndDate.clone().subtract(30, "days")
                    );
                  }
                }}
              >
                <MenuItem value='Daily'>Daily</MenuItem>
                <MenuItem value='Weekly'>Weekly</MenuItem>
                <MenuItem value='Monthly'>Monthly</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label='Num of Recurrences'
              variant='outlined'
              fullWidth
              type='number'
              value={recurrences}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (!isNaN(value) && value >= 0 && value <= 365) {
                  setRecurrences(value.toString());
                } else if (e.target.value === "") {
                  setRecurrences("");
                }
              }}
              margin='normal'
              inputProps={{ inputMode: "numeric", max: 365 }}
            />
            <TextField
              label='Additional Comments'
              variant='outlined'
              fullWidth
              multiline
              rows={4}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              margin='normal'
            />
            <p className='text-dark-mode'>Tracking Range:</p>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en'>
              <div className='send-report-datepickers'>
                <DatePicker
                  className='exec-date-picker'
                  label='Start'
                  value={rangeStartDate}
                  onChange={(date) => {
                    setRangeStartDate(date || Today);
                    // if (date && date.isBefore(Today, "day")) {
                    //   setRangeStartDate(Today);
                    // } else {
                    //   setRangeStartDate(date || Today);
                    // }

                    // Conditionally update rangeEndDate based on frequency
                    if (frequency === "Daily" && date) {
                      setRangeEndDate(date.clone().add(1, "days"));
                    } else if (frequency === "Weekly" && date) {
                      setRangeEndDate(date.clone().add(7, "days"));
                    } else if (frequency === "Monthly" && date) {
                      setRangeEndDate(date.clone().add(30, "days"));
                    }
                  }}
                />
                <span className='text-dark-mode'> to </span>
                <DatePicker
                  className='exec-date-picker'
                  label='End'
                  value={rangeEndDate}
                  onChange={(date) => {
                    // Handle the change of rangeEndDate
                    // if (date && date.isBefore(Today, "day")) {
                    //   setRangeEndDate(Today);
                    // } else if (
                    //   rangeStartDate &&
                    //   date &&
                    //   date.isBefore(rangeStartDate, "day")
                    // ) {
                    //   setRangeEndDate(rangeStartDate);
                    // } else {
                    setRangeEndDate(date || Today);
                    // }

                    // Conditionally update rangeStartDate based on frequency
                    if (frequency === "Daily" && date) {
                      setRangeStartDate(date.clone().subtract(1, "days"));
                    } else if (frequency === "Weekly" && date) {
                      setRangeStartDate(date.clone().subtract(7, "days"));
                    } else if (frequency === "Monthly" && date) {
                      setRangeStartDate(date.clone().subtract(30, "days"));
                    }
                  }}
                />
              </div>
            </LocalizationProvider>
            <p className='ops-send-report-resolution-matrics-title'>
              This Week:
            </p>
            <div className='ops-send-report-resolution-matrics'>
              <div className='ops-send-report-resolution-matrics-sub'>
                <span>Resolution Rate: </span>
                {typeof dashboardStats?.current_week?.resolution_rate ===
                "undefined" ? (
                  <Skeleton
                    width='50%'
                    height='80px'
                    style={{ marginLeft: "20px" }}
                  />
                ) : (
                  <>
                    <div className='exec-radio-values'>
                      <div className='exec-sidebar-number'>
                        <span className='slot'>
                          <SlotCounter
                            value={
                              dashboardStats?.current_week?.resolution_rate
                            }
                          />
                        </span>
                      </div>
                    </div>
                    <span className={`exec-sidebar-percentage`}>
                      <span
                        className={`${
                          parseFloat(
                            dashboardStats?.changes?.resolution_rate_change
                          ) === 0
                            ? "no-arrow"
                            : parseFloat(
                                dashboardStats?.changes?.resolution_rate_change
                              ) < 0
                            ? "red-arrow"
                            : "green-arrow"
                        } number`}
                      >
                        {parseFloat(
                          dashboardStats?.changes?.resolution_rate_change
                        ) === 0
                          ? "00.0%"
                          : (parseFloat(
                              dashboardStats?.changes?.resolution_rate_change
                            ) < 0
                              ? ""
                              : "") +
                            Math.abs(
                              parseFloat(
                                dashboardStats?.changes?.resolution_rate_change
                              )
                            ).toFixed(1) +
                            "%"}
                        {parseFloat(
                          dashboardStats?.changes?.resolution_rate_change
                        ) === 0
                          ? "⬅"
                          : parseFloat(
                              dashboardStats?.changes?.resolution_rate_change
                            ) < 0
                          ? "⬇"
                          : "⬆"}
                      </span>
                    </span>
                  </>
                )}
              </div>
              <div className='ops-send-report-resolution-matrics-sub'>
                <span className='text-dark-mode'>Sentiment Score​: </span>
                {typeof dashboardStats?.current_week?.sentiment_score ===
                "undefined" ? (
                  <Skeleton
                    width='50%'
                    height='80px'
                    style={{ marginLeft: "20px" }}
                  />
                ) : (
                  <>
                    <div className='exec-radio-values'>
                      <div className='exec-sidebar-number'>
                        <span className='slot'>
                          <SlotCounter
                            value={
                              dashboardStats?.current_week?.sentiment_score
                            }
                          />
                        </span>
                      </div>
                    </div>
                    <span className={`exec-sidebar-percentage`}>
                      <span
                        className={`${
                          parseFloat(
                            dashboardStats?.changes?.sentiment_score_change
                          ) === 0
                            ? "no-arrow"
                            : parseFloat(
                                dashboardStats?.changes?.sentiment_score_change
                              ) < 0
                            ? "red-arrow"
                            : "green-arrow"
                        } number`}
                      >
                        {parseFloat(
                          dashboardStats?.changes?.sentiment_score_change
                        ) === 0
                          ? "00.0%"
                          : (parseFloat(
                              dashboardStats?.changes?.sentiment_score_change
                            ) < 0
                              ? ""
                              : "") +
                            Math.abs(
                              parseFloat(
                                dashboardStats?.changes?.sentiment_score_change
                              )
                            ).toFixed(1) +
                            "%"}
                        {parseFloat(
                          dashboardStats?.changes?.sentiment_score_change
                        ) === 0
                          ? "⬅"
                          : parseFloat(
                              dashboardStats?.changes?.sentiment_score_change
                            ) < 0
                          ? "⬇"
                          : "⬆"}
                      </span>
                    </span>
                  </>
                )}
              </div>
            </div>
            <p className='ops-send-report-resolution-matrics-title'>
              Previous Week:
            </p>
            <div className='ops-send-report-resolution-matrics'>
              <div className='ops-send-report-resolution-matrics-sub'>
                <span>Resolution Rate: </span>
                {typeof dashboardStats?.previous_week?.resolution_rate ===
                "undefined" ? (
                  <Skeleton
                    width='50%'
                    height='80px'
                    style={{ marginLeft: "20px" }}
                  />
                ) : (
                  <div className='exec-radio-values'>
                    <div className='exec-sidebar-number'>
                      <span className='slot'>
                        <SlotCounter
                          value={dashboardStats?.previous_week?.resolution_rate}
                        />
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div className='ops-send-report-resolution-matrics-sub'>
                <span className='text-dark-mode'>Sentiment Score​: </span>
                {typeof dashboardStats?.previous_week?.sentiment_score ===
                "undefined" ? (
                  <Skeleton
                    width='50%'
                    height='80px'
                    style={{ marginLeft: "20px" }}
                  />
                ) : (
                  <div className='exec-radio-values'>
                    <div className='exec-sidebar-number'>
                      <span className='slot'>
                        <SlotCounter
                          value={dashboardStats?.previous_week?.sentiment_score}
                        />
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {excelFile && (
              <div className='table-file-export'>
                <p onClick={handlePreview}>Preview Excel File</p>
                {/* <input type="hidden" name="data" value={excelFile} /> */}
              </div>
            )}
          </div>
          <div>
            <Button
              className='exec-send-btn'
              variant='contained'
              onClick={handleSendReport}
            >
              Send
            </Button>
          </div>
        </Box>
      </Modal>
      {/* <ReactTooltip
        id='ops-search-tooltip'
        style={{
          borderRadius: "5px",
          ...(mode === "dark" && {
            backgroundColor: "#f5f5f5",
            color: "black",
          }),
        }}
        place='top'
        content='Search for a specific type of call'
      /> */}
    </div>
  );
}

export default Search;
